// apps/vendor/src/pages/application/ApplicationStore.ts
//
// Phase 1 Wave 12 Task 99 — vendor 5-step application state.
//
// . Persists to sessionStorage so refresh mid-step doesn't
// lose user input. Cleared on successful submit (PendingReviewPage).

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type BusinessType = '' | 'individual' | 'business';
export type StoreType = '' | 't-shirts' | 'mugs' | 'mixed';

export interface BusinessInfo {
  businessType: BusinessType;
  fullName: string;
  email: string;
  phone: string;
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export interface StoreSetup {
  storeName: string;
  storeType: StoreType;
  capacityPerDay: string;
  equipment: string[];
}

export interface DocumentRef {
  name: string;
  size: number;
  /**
   * P0-2 (vendor-audit 2026-08-02): KYC document id returned by
   * POST /api/v1/vendor/kyc/documents at select time. Null for slots that
   * have no matching KYC doc_type (insurance) — those stay metadata-only.
   * Persisting the id (not the File) survives a mid-wizard refresh.
   */
  docId?: string | null;
  docType?: string | null;
}

export interface Documents {
  businessLicense: DocumentRef | null;
  taxId: DocumentRef | null;
  insuranceCertificate: DocumentRef | null;
}

export interface BankDetails {
  accountHolderName: string;
  bankName: string;
  accountNumber: string;
  routingNumber: string;
  confirmAccountNumber: string;
}

export interface ApplicationFormData {
  businessInfo: BusinessInfo;
  storeSetup: StoreSetup;
  documents: Documents;
  bankDetails: BankDetails;
  termsAccepted: boolean;
}

export const APPLICATION_DEFAULT: ApplicationFormData = {
  businessInfo: {
    businessType: '',
    fullName: '',
    email: '',
    phone: '',
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
  },
  storeSetup: {
    storeName: '',
    storeType: '',
    capacityPerDay: '',
    equipment: [],
  },
  documents: {
    businessLicense: null,
    taxId: null,
    insuranceCertificate: null,
  },
  bankDetails: {
    accountHolderName: '',
    bankName: '',
    accountNumber: '',
    routingNumber: '',
    confirmAccountNumber: '',
  },
  termsAccepted: false,
};

interface ApplicationState {
  data: ApplicationFormData;
  /**
   * Idempotency-Key persisted across retries. Generated once on first submit;
   * all retries reuse the same key so BE can dedupe properly. Cleared on
   * reset(). Wave 12 CrossAI fix.
   */
  submitIdempotencyKey: string | null;
  setBusinessInfo: (patch: Partial<BusinessInfo>) => void;
  setStoreSetup: (patch: Partial<StoreSetup>) => void;
  setDocument: (slot: keyof Documents, ref: DocumentRef | null) => void;
  setBankDetails: (patch: Partial<BankDetails>) => void;
  toggleEquipment: (item: string) => void;
  setTermsAccepted: (v: boolean) => void;
  /** Get-or-generate persistent idempotency key for application submit. */
  getOrCreateSubmitKey: () => string;
  reset: () => void;
}

function newIdemKey(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export const useApplicationStore = create<ApplicationState>()(
  persist(
    (set, get) => ({
      data: APPLICATION_DEFAULT,
      submitIdempotencyKey: null,
      setBusinessInfo: (patch) =>
        set((s) => ({
          data: { ...s.data, businessInfo: { ...s.data.businessInfo, ...patch } },
        })),
      setStoreSetup: (patch) =>
        set((s) => ({
          data: { ...s.data, storeSetup: { ...s.data.storeSetup, ...patch } },
        })),
      setDocument: (slot, ref) =>
        set((s) => ({
          data: { ...s.data, documents: { ...s.data.documents, [slot]: ref } },
        })),
      setBankDetails: (patch) =>
        set((s) => ({
          data: { ...s.data, bankDetails: { ...s.data.bankDetails, ...patch } },
        })),
      toggleEquipment: (item) =>
        set((s) => {
          const eq = new Set(s.data.storeSetup.equipment);
          if (eq.has(item)) eq.delete(item);
          else eq.add(item);
          return {
            data: {
              ...s.data,
              storeSetup: { ...s.data.storeSetup, equipment: Array.from(eq) },
            },
          };
        }),
      setTermsAccepted: (v) =>
        set((s) => ({ data: { ...s.data, termsAccepted: v } })),
      getOrCreateSubmitKey: () => {
        const existing = get().submitIdempotencyKey;
        if (existing) return existing;
        const key = newIdemKey('vendor-app');
        set({ submitIdempotencyKey: key });
        return key;
      },
      reset: () =>
        set({ data: APPLICATION_DEFAULT, submitIdempotencyKey: null }),
    }),
    {
      name: 'pw-vendor-application',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);
